import { axiosInstance } from "../axios/axiosHandler";
import { EndPoints } from "../axios/endPoints";
import { authHeader } from "../store/auth-slice/auth-slice";

const FALLBACK_API_ERROR_KEY = "BE_COMMON_UNHANDLED_ERROR";

export interface CustomerPortalSessionRequest {
  returnUrl: string;
}

export interface SubscriptionActionRequest {
  localSubscriptionId: string;
}

export interface CreatePaidSubscriptionRequest {
  subscriptionName: string;
  stripePriceId: string;
  modelsAllowed?: number | null;
  successUrl: string;
  cancelUrl: string;
}

export interface StripeCheckoutSessionResponse {
  localSubscriptionId: string;
  sessionId: string;
  checkoutUrl: string;
}

export interface AvailableSubscriptionPlanResponse {
  stripePriceId: string;
  displayName: string;
  subscriptionName: string;
  modelsAllowed?: number | null;
  trialSupported?: boolean;
  trialAvailableForUser?: boolean;
  trialDuration?: string;
  unitAmount?: number | null;
  currency: string;
  billingInterval?: string | null;
  billingIntervalCount?: number | null;
}

export interface SubscriptionBillingStateResponse {
  hasActivePaidSubscription: boolean;
  hasActiveTrialSubscription: boolean;
  localSubscriptionId?: string | null;
  stripeProductId?: string | null;
  subscriptionStripeProductId?: string | null;
  subscriptionName?: string | null;
  subscriptionStatus?: string | null;
  startDateUtc?: string | null;
  endDateUtc?: string | null;
  isCancelAtPeriodEnd?: boolean;
  cancelsAtUtc?: string | null;
  trialEndsAtUtc?: string | null;
  nextBilling?: {
    billingDateUtc?: string | null;
    stripeProductId?: string | null;
    subscriptionName?: string | null;
  } | null;
  pendingTierPriceChange?: {
    currentStripePriceId?: string | null;
    nextStripePriceId?: string | null;
    currentPriceDisplay?: string | null;
    nextPriceDisplay?: string | null;
    appliesOnUtc?: string | null;
    appliesInDays?: number;
  } | null;
}

export interface SubscriptionInvoiceResponse {
  stripeInvoiceId: string;
  invoiceNumber?: string | null;
  status?: string | null;
  amountDue?: number | null;
  amountPaid?: number | null;
  amountRemaining?: number | null;
  currency?: string | null;
  createdUtc: string;
  dueDateUtc?: string | null;
  paidAtUtc?: string | null;
  hostedInvoiceUrl?: string | null;
  invoicePdfUrl?: string | null;
}

export interface CustomerPortalSessionResponse {
  portalUrl: string;
}

export interface TierManagementPlanResponse {
  stripePriceId: string;
  stripeProductId: string;
  name: string;
  modelsAllowed?: number | null;
  trialSupported?: boolean;
  trialDuration?: string;
  description?: string | null;
  unitAmount?: number | null;
  currency: string;
  billingInterval?: string | null;
  billingIntervalCount?: number | null;
  active: boolean;
}

export interface UpdateTierPlanRequest {
  stripePriceId: string;
  name: string;
  modelsAllowed?: number | null;
  trialSupported?: boolean;
  trialDuration?: string;
  description?: string | null;
  unitAmount: number;
}

export interface TierPlanUpdateResultResponse {
  previousStripePriceId: string;
  newStripePriceId: string;
  updatedSubscriptionsCount: number;
}

export type ApiResponse<T> = {
  success: boolean;
  message?: string | null;
  result: T | null;
};

export function getApiResponseMessage(
  responseMessage?: string | null,
  fallback: string = FALLBACK_API_ERROR_KEY,
): string {
  if (
    typeof responseMessage === "string" &&
    responseMessage.trim().length > 0
  ) {
    return responseMessage.trim();
  }

  return fallback;
}

export function getApiErrorMessage(
  error: unknown,
  fallback: string = FALLBACK_API_ERROR_KEY,
): string {
  if (typeof error === "string" && error.trim().length > 0) {
    return error.trim();
  }

  if (error && typeof error === "object") {
    const errorWithMessage = error as { message?: unknown };
    if (
      typeof errorWithMessage.message === "string" &&
      errorWithMessage.message.trim().length > 0
    ) {
      return errorWithMessage.message.trim();
    }

    const errorWithResponse = error as { response?: { data?: unknown } };
    const responseData = errorWithResponse.response?.data;
    if (typeof responseData === "string" && responseData.trim().length > 0) {
      return responseData.trim();
    }

    if (responseData && typeof responseData === "object") {
      const dataMessage = (responseData as { message?: unknown }).message;
      if (typeof dataMessage === "string" && dataMessage.trim().length > 0) {
        return dataMessage.trim();
      }
    }
  }

  return fallback;
}

export async function getAvailableSubscriptionPlans(): Promise<
  ApiResponse<AvailableSubscriptionPlanResponse[]>
> {
  const response = await axiosInstance.get<
    ApiResponse<AvailableSubscriptionPlanResponse[]>
  >(EndPoints.SUBSCRIPTION_AVAILABLE_PLANS, authHeader());

  return response.data;
}

export async function getCurrentSubscriptionBillingState(): Promise<
  ApiResponse<SubscriptionBillingStateResponse>
> {
  const response = await axiosInstance.get<
    ApiResponse<SubscriptionBillingStateResponse>
  >(EndPoints.SUBSCRIPTION_CURRENT_BILLING_STATE, authHeader());

  return response.data;
}

export async function getTierManagementPlans(): Promise<
  ApiResponse<TierManagementPlanResponse[]>
> {
  const response = await axiosInstance.get<
    ApiResponse<TierManagementPlanResponse[]>
  >(EndPoints.SUBSCRIPTION_TIER_MANAGEMENT_PLANS, authHeader());

  return response.data;
}

export async function getUserSubscriptionInvoices(): Promise<
  ApiResponse<SubscriptionInvoiceResponse[]>
> {
  const response = await axiosInstance.get<
    ApiResponse<SubscriptionInvoiceResponse[]>
  >(EndPoints.SUBSCRIPTION_USER_INVOICES, authHeader());

  return response.data;
}

export async function updateTierManagementPlan(
  body: UpdateTierPlanRequest,
): Promise<ApiResponse<TierPlanUpdateResultResponse>> {
  const response = await axiosInstance.post<
    ApiResponse<TierPlanUpdateResultResponse>
  >(EndPoints.SUBSCRIPTION_TIER_MANAGEMENT_UPDATE, body, authHeader());

  return response.data;
}

export async function createCustomerPortalSession(
  body: CustomerPortalSessionRequest,
): Promise<ApiResponse<CustomerPortalSessionResponse>> {
  const response = await axiosInstance.post<
    ApiResponse<CustomerPortalSessionResponse>
  >(EndPoints.SUBSCRIPTION_CUSTOMER_PORTAL, body, authHeader());

  return response.data;
}

export async function createPaidSubscription(
  body: CreatePaidSubscriptionRequest,
): Promise<ApiResponse<StripeCheckoutSessionResponse>> {
  const response = await axiosInstance.post<
    ApiResponse<StripeCheckoutSessionResponse>
  >(EndPoints.SUBSCRIPTION_CREATE_PAID, body, authHeader());

  return response.data;
}

export async function cancelPaidSubscription(
  body: SubscriptionActionRequest,
): Promise<ApiResponse<SubscriptionBillingStateResponse>> {
  const response = await axiosInstance.post<
    ApiResponse<SubscriptionBillingStateResponse>
  >(EndPoints.SUBSCRIPTION_CANCEL_PAID, body, authHeader());

  return response.data;
}

export async function continuePaidSubscription(
  body: SubscriptionActionRequest,
): Promise<ApiResponse<SubscriptionBillingStateResponse>> {
  const response = await axiosInstance.post<
    ApiResponse<SubscriptionBillingStateResponse>
  >(EndPoints.SUBSCRIPTION_CONTINUE_PAID, body, authHeader());

  return response.data;
}
